import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useGetLoanDefaultTerms, useUpdateLoanDefaultTerms } from '../../../services/loanService';
import { useChargeControls, useSetChargesStopped } from '../../../services/chargeControls';
import { formatSouthAfricanDateTime } from '../../../utils/southAfricanTime';
import type { DefaultChargeFrequency } from '../../../services/schemas/loanServiceSchemas/defaultChargeFrequency';
import {
  DEFAULT_CHARGE_FREQUENCY_LABELS,
  describeChargeFrequency,
} from '../../loan-application/steps/DefaultTermsFields';

interface Props {
  accountId: string;
  /** Called after saving, so the page can refresh charges a new grace period withdrew. */
  onSaved?: () => void;
}

/**
 * The loan's default terms where the business decides on charges: the rate, how often it is
 * charged, and the grace period. Terms left out at origination can be set here, and a grace period
 * granted here withdraws the pending charges it covers.
 */
const LoanDefaultTermsCard: React.FC<Props> = ({ accountId, onSaved }) => {
  const { data: terms, refetch } = useGetLoanDefaultTerms(accountId, { query: { enabled: !!accountId } });
  const updateTerms = useUpdateLoanDefaultTerms();
  // Stop automatic charges on this loan, and whether its terms are its own (#37, #38)
  const { data: controls, refetch: refetchControls } = useChargeControls(accountId);
  const setStopped = useSetChargesStopped(accountId);
  const [stopping, setStopping] = useState(false);
  const [stopReason, setStopReason] = useState('');
  const toggleCharges = async (stopped: boolean) => {
    if (stopped && !stopReason.trim()) { toast.error('Give a reason for stopping charges on this loan'); return; }
    try {
      await setStopped.mutateAsync({ stopped, reason: stopped ? stopReason.trim() : undefined });
      toast.success(stopped ? 'Automatic charges stopped on this loan' : 'Automatic charges restarted');
      setStopping(false); setStopReason('');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'The change could not be saved');
    }
  };

  const [editing, setEditing] = useState(false);
  const [rate, setRate] = useState('');
  const [frequency, setFrequency] = useState('');
  const [graceDays, setGraceDays] = useState('');
  const [intervalDays, setIntervalDays] = useState('');

  useEffect(() => {
    setRate(terms?.defaultInterestRate != null ? String(terms.defaultInterestRate) : '');
    setFrequency(terms?.defaultChargeFrequency || '');
    setGraceDays(terms?.defaultGraceDays != null ? String(terms.defaultGraceDays) : '');
    setIntervalDays(terms?.defaultChargeIntervalDays != null ? String(terms.defaultChargeIntervalDays) : '');
  }, [terms, editing]);

  const save = async () => {
    const rateValue = rate === '' ? undefined : Number(rate);
    const graceValue = graceDays === '' ? undefined : Number(graceDays);
    const intervalValue = frequency === 'CUSTOM_DAYS' && intervalDays !== '' ? Number(intervalDays) : undefined;
    if (frequency === 'CUSTOM_DAYS' && (intervalValue === undefined || !Number.isInteger(intervalValue) || intervalValue < 1)) {
      toast.error('Enter how many days apart the default interest is charged');
      return;
    }
    if ((rateValue !== undefined && (isNaN(rateValue) || rateValue < 0))
        || (graceValue !== undefined && (!Number.isInteger(graceValue) || graceValue < 0))) {
      toast.error('Rate and grace days must be zero or more (grace in whole days)');
      return;
    }

    try {
      const saved = await updateTerms.mutateAsync({
        accountId,
        data: {
          defaultInterestRate: rateValue,
          defaultChargeFrequency: (frequency || undefined) as DefaultChargeFrequency | undefined,
          defaultChargeIntervalDays: intervalValue,
          defaultGraceDays: graceValue,
        },
      });
      const withdrawn = saved?.withdrawnCharges || 0;
      toast.success(withdrawn > 0
        ? `Default terms saved. ${withdrawn} pending charge${withdrawn === 1 ? '' : 's'} withdrawn - within the grace period.`
        : 'Default terms saved');
      setEditing(false);
      await refetch();
      refetchControls();
      onSaved?.();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save default terms');
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">
          Default terms
          {controls?.personalTerms && <span className="badge bg-info text-dark ms-2 align-middle" style={{ fontSize: '0.7rem' }}>Personal to this loan</span>}
        </h5>
        {!editing && (
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setEditing(true)}>
            <i className="fa fa-pencil me-1"></i>Edit
          </button>
        )}
      </div>
      <div className="card-body">
        {!editing ? (
          <div className="row g-3">
            <div className="col-md-4">
              <h6 className="text-muted mb-1 small">Default interest rate</h6>
              <div className="fw-semibold">
                {terms?.defaultInterestRate != null ? `${terms.defaultInterestRate}% per period` : "Loan's interest rate (not set)"}
              </div>
            </div>
            <div className="col-md-4">
              <h6 className="text-muted mb-1 small">Charged</h6>
              <div className="fw-semibold text-capitalize">
                {describeChargeFrequency(terms?.defaultChargeFrequency, terms?.defaultChargeIntervalDays)}
              </div>
            </div>
            <div className="col-md-4">
              <h6 className="text-muted mb-1 small">Grace period</h6>
              <div className="fw-semibold">
                {terms?.defaultGraceDays ? `${terms.defaultGraceDays} days after the due date` : 'None'}
              </div>
            </div>
            <div className="col-12">
              {controls?.chargesStopped ? (
                <div className="alert alert-warning d-flex flex-wrap align-items-center gap-2 mb-0 py-2">
                  <i className="fa fa-pause-circle"></i>
                  <span>
                    <strong>Automatic charges are stopped</strong>: {controls.chargesStoppedReason}
                    {controls.chargesStoppedBy ? ` · ${controls.chargesStoppedBy}` : ''}
                    {controls.chargesStoppedAt ? ` · ${formatSouthAfricanDateTime(controls.chargesStoppedAt)}` : ''}
                  </span>
                  <button type="button" className="btn btn-sm btn-outline-dark ms-auto" onClick={() => toggleCharges(false)} disabled={setStopped.isPending}>
                    Restart charges
                  </button>
                </div>
              ) : stopping ? (
                <div className="d-flex flex-wrap align-items-end gap-2">
                  <div className="flex-grow-1">
                    <label htmlFor="stopReason" className="form-label small mb-1">Why stop automatic charges on this loan?</label>
                    <input id="stopReason" type="text" maxLength={500} className="form-control form-control-sm" value={stopReason}
                      placeholder="e.g. Payment arrangement agreed until 30 November" onChange={e => setStopReason(e.target.value)} />
                  </div>
                  <button type="button" className="btn btn-sm btn-warning" onClick={() => toggleCharges(true)} disabled={setStopped.isPending}>Stop charges</button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setStopping(false); setStopReason(''); }}>Cancel</button>
                </div>
              ) : (
                <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => setStopping(true)}>
                  <i className="fa fa-pause me-1"></i>Stop automatic charges on this loan
                </button>
              )}
              <div className="form-text">While stopped, the nightly run proposes no charges for this loan. Charges already approved stay.</div>
            </div>
          </div>
        ) : (
          <>
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label htmlFor="termsRate" className="form-label">Default rate (%)</label>
                <input id="termsRate" type="number" min={0} step="0.01" className="form-control"
                  placeholder="Blank = loan's interest rate" value={rate} onChange={e => setRate(e.target.value)} />
              </div>
              <div className="col-md-3">
                <label htmlFor="termsFrequency" className="form-label">Charged</label>
                <select id="termsFrequency" className="form-select" value={frequency} onChange={e => setFrequency(e.target.value)}>
                  <option value="">Monthly (default)</option>
                  {Object.entries(DEFAULT_CHARGE_FREQUENCY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              {frequency === 'CUSTOM_DAYS' && (
                <div className="col-md-3">
                  <label htmlFor="termsInterval" className="form-label">Every (days)</label>
                  <input id="termsInterval" type="number" min={1} step={1} className="form-control"
                    placeholder="e.g. 15" value={intervalDays} onChange={e => setIntervalDays(e.target.value)} />
                </div>
              )}
              <div className="col-md-3">
                <label htmlFor="termsGrace" className="form-label">Grace period (days)</label>
                <input id="termsGrace" type="number" min={0} step={1} className="form-control"
                  placeholder="0" value={graceDays} onChange={e => setGraceDays(e.target.value)} />
              </div>
            </div>
            <p className="text-muted small mt-3 mb-3">
              Pending charges the grace period covers are withdrawn. If the client is still unpaid when
              the grace period ends, the charge is proposed again.
            </p>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={updateTerms.isPending}>
                {updateTerms.isPending ? 'Saving...' : 'Save terms'}
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setEditing(false)}
                disabled={updateTerms.isPending}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoanDefaultTermsCard;
